/**
 * VideoSection — Guest-facing invite film player.
 *
 * Behaviour:
 *  - Fetches video_url from /api/public/guest-site-config
 *  - Native <video> element — full playback control, no third-party embeds
 *  - Audio fade-out: begins at FADE_START_TIME (122s) over FADE_DURATION_MS (5s)
 *    exactly matching the invite film timing (2:02 → 2:07)
 *  - After fade completes → smooth-scrolls to #rsvp-module
 *  - Tap anywhere on video to pause; tap again to resume
 *  - Responsive max-width via useWindowSize hook
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useWindowSize } from '../hooks/useWindowSize';

const API              = process.env.REACT_APP_BACKEND_URL;
const VIDEO_URL        = 'https://customer-assets.emergentagent.com/job_96f73dc0-b292-41dd-a366-8b41de2ebdcc/artifacts/sj38t5q9_4.9.26%20Invitation%20Video.mp4';
const FADE_START_TIME  = 122;   // audio fade begins at 2:02 (matches invite film timing)
const FADE_DURATION_MS = 5000;  // 5-second fade-out window (122s → 127s)
const GOLD             = 'rgba(212,168,83,0.80)';

// ─── Scroll helper — works inside the overflowY:auto container ────────────────
function scrollToRSVP() {
  const el = document.getElementById('rsvp-welcome-header');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

// ─── Main component ───────────────────────────────────────────────────────────
const VideoSection = () => {
  const { isTablet, isDesktop }  = useWindowSize();
  const maxW                     = isDesktop ? 760 : isTablet ? 580 : 360;

  const videoRef   = useRef(null);
  const fadeRaf    = useRef(null);      // requestAnimationFrame handle
  const fadingRef  = useRef(false);     // prevents double-triggering
  const hasStarted = useRef(false);     // true once playback has started at least once

  const [videoUrl,      setVideoUrl]      = useState('');
  const [playing,       setPlaying]       = useState(false);
  const [playRequested, setPlayRequested] = useState(false);
  const [buffering,     setBuffering]     = useState(false);
  const [fadingOut,     setFadingOut]     = useState(false);
  const [transitioned,  setTransitioned]  = useState(false);

  // ── Fetch video URL from config ──────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/api/public/guest-site-config`)
      .then(r => r.json())
      .then(d => {
        if (d?.video_url) {
          const url = d.video_url.startsWith('http')
            ? d.video_url
            : `${API}${d.video_url}`;
          setVideoUrl(url);
        }
      })
      .catch(() => {});
  }, []);

  // ── Cancel any in-progress RAF on unmount ────────────────────────────────
  useEffect(() => () => { if (fadeRaf.current) cancelAnimationFrame(fadeRaf.current); }, []);

  // ── Smooth volume fade via rAF ───────────────────────────────────────────
  const startFade = useCallback(() => {
    if (fadingRef.current) return;
    fadingRef.current = true;
    setFadingOut(true);

    const video      = videoRef.current;
    const startVol   = video.volume;
    const fadeDur    = FADE_DURATION_MS;
    const startStamp = performance.now();

    const animate = (now) => {
      if (!videoRef.current) return;
      const elapsed  = now - startStamp;
      const progress = Math.min(elapsed / fadeDur, 1);

      videoRef.current.volume = Math.max(0, startVol * (1 - progress));

      if (progress < 1) {
        fadeRaf.current = requestAnimationFrame(animate);
      } else {
        // Fade complete — transition to RSVP
        fadeRaf.current = null;
        if (!transitioned) {
          setTransitioned(true);
          setTimeout(scrollToRSVP, 200);
        }
      }
    };

    fadeRaf.current = requestAnimationFrame(animate);
  }, [transitioned]);

  // ── timeupdate: trigger fade at 122s (matching invite film timing) ───────
  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v || fadingRef.current || transitioned) return;
    if (isFinite(v.currentTime) && v.currentTime >= FADE_START_TIME) {
      startFade();
    }
  }, [startFade, transitioned]);

  // ── onEnded: safety net if fade didn't fire ──────────────────────────────
  const handleEnded = useCallback(() => {
    setPlaying(false);
    setPlayRequested(false);
    setBuffering(false);
    if (!transitioned) {
      setTransitioned(true);
      scrollToRSVP();
    }
  }, [transitioned]);

  // ── Play from start ──────────────────────────────────────────────────────
  const handlePlay = () => {
    if (!videoRef.current) return;
    videoRef.current.volume = 1;
    fadingRef.current = false;
    setFadingOut(false);
    setTransitioned(false);
    setPlayRequested(true);
    setBuffering(false);
    hasStarted.current = true;
    videoRef.current.play().catch(() => setPlayRequested(false));
  };

  // ── Tap-to-pause / resume during playback ────────────────────────────────
  const handleVideoTap = useCallback(() => {
    if (!videoRef.current) return;
    if (!videoRef.current.paused) {
      videoRef.current.pause();
    } else if (videoRef.current.currentTime > 0 && !videoRef.current.ended) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div id="video" className="card-h-pad" style={{
      width: '100%',
      paddingTop: 16,
      paddingBottom: 16,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      {/* Video wrapper — 16:9 enforced via paddingBottom trick */}
      <div style={{ width: '100%', maxWidth: maxW, position: 'relative' }}>
        <div data-testid="video-landscape" style={{
          width: '100%',
          paddingBottom: '56.25%',
          position: 'relative',
          borderRadius: 14,
          overflow: 'hidden',
          background: '#0D0B08',
          boxShadow: '0 6px 24px rgba(0,0,0,0.28), 0 0 0 2.5px rgba(212,168,83,0.70), 0 0 20px rgba(212,168,83,0.22)',
        }}>

          {/* ── Actual video element ── */}
          {videoUrl ? (
            <video
              ref={videoRef}
              src={VIDEO_URL}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              onPlaying={() => { setPlaying(true); setPlayRequested(false); setBuffering(false); }}
              onPause={() => { setPlaying(false); setPlayRequested(false); setBuffering(false); }}
              onWaiting={() => setBuffering(true)}
              playsInline
              preload="metadata"
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                transition: 'opacity 0.5s ease',
                opacity: fadingOut ? 0.85 : 1,
              }}
            />
          ) : (
            /* No URL yet — placeholder */
            <div style={{ position: 'absolute', inset: 0,
              background: 'linear-gradient(145deg,#1a1a1a 0%,#0d0d0d 100%)' }}>
              <div style={{ position: 'absolute', inset: 0, opacity: 0.04,
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cpath d='M100 20 Q120 50 100 80 Q80 50 100 20' fill='none' stroke='%23D4A853' stroke-width='1'/%3E%3Cpath d='M60 60 Q90 80 100 100 Q70 80 60 60' fill='none' stroke='%23D4A853' stroke-width='1'/%3E%3Cpath d='M140 60 Q110 80 100 100 Q130 80 140 60' fill='none' stroke='%23D4A853' stroke-width='1'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'repeat', backgroundSize: '120px 120px' }} />
            </div>
          )}

          {/* ── Tap-to-pause transparent overlay — active during playback ── */}
          {playing && !buffering && (
            <div
              onClick={handleVideoTap}
              style={{
                position: 'absolute', inset: 0,
                cursor: 'pointer',
                background: 'transparent',
                zIndex: 7,
              }}
            />
          )}

          {/* ── Pre-play overlay: play button → spinner until onPlaying fires ── */}
          {!playing && (
            <div
              onClick={(!playRequested && videoUrl) ? handlePlay : undefined}
              style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                cursor: (!playRequested && videoUrl) ? 'pointer' : 'default',
                background: 'rgba(13,11,8,0.35)',
                transition: 'background 0.3s ease',
              }}
            >
              {playRequested ? (
                /* Loading state — shown from tap until first frame renders */
                <>
                  <div style={{
                    width: 36, height: 36,
                    border: `2px solid rgba(212,168,83,0.22)`,
                    borderTopColor: GOLD,
                    borderRadius: '50%',
                    animation: 'videoSpinner 0.9s linear infinite',
                    marginBottom: 10,
                  }} />
                  <p style={{
                    fontFamily: "'Cinzel', serif",
                    fontSize: 11, fontWeight: 700,
                    letterSpacing: '2px', textTransform: 'uppercase',
                    color: GOLD, margin: 0,
                  }}>
                    Loading Film…
                  </p>
                </>
              ) : (
                /* Play / Resume button state */
                <>
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    border: `1.5px solid ${GOLD}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 0 32px rgba(212,168,83,0.25)`,
                    marginBottom: 12,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    animation: 'playButtonPulse 2.4s ease-in-out infinite',
                  }}>
                    <div style={{
                      width: 0, height: 0,
                      borderTop: '12px solid transparent',
                      borderBottom: '12px solid transparent',
                      borderLeft: `20px solid ${GOLD}`,
                      marginLeft: 5,
                    }} />
                  </div>
                  <p style={{
                    fontFamily: "'Cinzel', serif",
                    fontSize: 11, fontWeight: 700,
                    letterSpacing: '2px', textTransform: 'uppercase',
                    color: GOLD, margin: 0,
                  }}>
                    {!videoUrl
                      ? 'Film Coming Soon'
                      : (videoRef.current?.currentTime > 0 ? 'Resume Film' : 'Play Film')
                    }
                  </p>
                </>
              )}
            </div>
          )}

          {/* ── Mid-play buffering indicator — video stalled after first frame ── */}
          {playing && buffering && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.28)',
              pointerEvents: 'none',
            }}>
              <div style={{
                width: 32, height: 32,
                border: `2px solid rgba(212,168,83,0.22)`,
                borderTopColor: GOLD,
                borderRadius: '50%',
                animation: 'videoSpinner 0.9s linear infinite',
              }} />
            </div>
          )}

          {/* ── Fade-out visual cue — subtle dark vignette ── */}
          {fadingOut && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0)',
              animation: 'videoFadeOut 3s ease forwards',
              pointerEvents: 'none',
            }} />
          )}

        </div>
      </div>

      {/* Keyframes for video animations */}
      <style>{`
        @keyframes videoFadeOut {
          from { background: rgba(0,0,0,0); }
          to   { background: rgba(0,0,0,0.55); }
        }
        @keyframes videoSpinner {
          to { transform: rotate(360deg); }
        }
        @keyframes playButtonPulse {
          0%, 100% { box-shadow: 0 0 24px rgba(212,168,83,0.18); transform: scale(1); }
          50%       { box-shadow: 0 0 40px rgba(212,168,83,0.38); transform: scale(1.04); }
        }
      `}</style>
    </div>
  );
};

export default VideoSection;
